import AppAlertComponent from "@/app/common/components/AppAlertComponent/AppAlertComponent";
import { CardContent } from "@/app/common/components/Card";
import { cn } from "@/app/common/utilities";
import { Message } from "@/schemas/message.schema";
import { PaymentMessage } from "@/schemas/payments.schema";
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { useEffect, useRef } from "react";

interface ConversationProps {
    messages: Message[] | PaymentMessage[]
    chatAreaHeight: number
}

const TranslatableSystemMessages = [
    'Decision: accepted',
    'Decision: rejected',
    'Reason: Based on program rules',
    'Reason: Based on evidence',
    'Reason: Other',
]

export default function Conversation({ messages, chatAreaHeight }: ConversationProps) {
    const { t: translate } = useTranslation()
    const DATE_FNS_BASE_FORMAT = 'yyyy-MM-dd hh:mmaa'
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView()
    }, [messages])

    function getTranslatedMessage(message: string) {
        return TranslatableSystemMessages.reduce((updatedMessage, phrase) => {
            if (updatedMessage.includes(phrase)) {
                return updatedMessage.replace(phrase, translate(phrase))
            }
            return updatedMessage
        }, message)
    }

    const formatMessageText = (text: string) => {
        const cleanedText = text.replace(/\n{3,}/g, '\n\n')

        return cleanedText.split('\n').map((paragraph, index) => (
            <p key={index} className={index > 0 ? 'mt-2' : ''}>
                {paragraph}
            </p>
        ))
    }

    function formatDateSent(timestamp: string) {
        const utcString = timestamp.replace(' ', 'T') + 'Z' // Convert to "2025-01-02T21:30:39Z"
        const newDate = parseISO(utcString)

        const response = format(newDate, DATE_FNS_BASE_FORMAT)

        return response
    }

    return (
        <CardContent className="flex-1 h-full flex flex-col h-full p-0 pt-0 mt-0 mb-0 pb-0">
            <div
                style={{
                    maxHeight: chatAreaHeight,
                }}
                className={cn(
                    'flex-1 overflow-y-auto max-h-auto space-y-4 px-4 items-center',
                    messages?.length == 0 ? 'flex justify-center' : ''
                )}
            >
                {messages?.length == 0 && (
                    <AppAlertComponent
                        className="flex"
                        children={null}
                        title={translate('you_have_no_messave_title')}
                        text={translate('you_have_no_messave_body')}
                        variant="info"
                        needHelp
                    ></AppAlertComponent>
                )}
                {messages?.map((message) => (
                    <div key={`${message.message}-${message.id}`}>
                        <div
                            className={`text-sm whitespace-pre-wrap break-words rounded-lg p-3 bg-gray-100 border border-gray-200 text-gray-800`}
                        >
                            <div className="message-content">
                                {formatMessageText(
                                    getTranslatedMessage(message.message)
                                )}
                            </div>
                            <span className="text-xs opacity-75 block mt-2 pt-1 border-current">
                                {formatDateSent(message.created_datetime)}
                            </span>
                        </div>
                    </div>
                ))}
                <div className="h-0 m-0 p-0 " ref={messagesEndRef} />
            </div>
        </CardContent>
    )
}
